'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { AlertDot } from '#pplx/frontend/aether/components/AlertDot/index.js';
import { sidebarSubmenuLinkClickedEvent } from '#pplx/frontend/apps/ask/events/sidebar/analytics.js';
import { getRecentThreadInteractionTarget, getSidebarThreadIdentity, } from '#pplx/frontend/apps/ask/hooks/sidebar-thread-actions/sidebarThreadIdentity.js';
import { useSidebarThreadInteractionsFactory } from '#pplx/frontend/apps/ask/hooks/sidebar-thread-actions/useSidebarThreadInteractions.js';
import { useDeleteThreadConfirmation } from '#pplx/frontend/apps/ask/hooks/threads-hooks/useDeleteThreadConfirmation.js';
import { usePrefetchThreadEntries } from '#pplx/frontend/apps/ask/hooks/threads-hooks/usePrefetchThreadEntries.js';
import { useSidebarThreadActions } from '#pplx/frontend/apps/ask/hooks/threads-hooks/useSidebarThreadActions.js';
import { SessionItem } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/SessionItem.js';
import { isComputerTaskThreadRoute } from '#pplx/frontend/apps/ask/layout/sidebar/core/shared/constants.js';
import { getDisplayTitle } from '#pplx/frontend/apps/ask/markdown/markdown.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
function getThreadItemAccessory({ unread }) {
    if (unread)
        return _jsx(AlertDot, { size: "sm" });
    return null;
}
export const ThreadItem = React.memo(function ThreadItem({ item, onDeleteClick, sidebarOpen, isInPopover, additionalMenuItems, hoverDetailsLookupEntryUuid, collapsed, foreground, groupedHistoryEnabled, }) {
    const { title, link, isStreaming, unread } = item;
    const { isThread, contextUUID, collectionUUID } = getSidebarThreadIdentity(item);
    const isComputerTask = isComputerTaskThreadRoute(link);
    const displayTitle = getDisplayTitle(title);
    const { $t } = useIntl();
    const { track } = useInstrumentation();
    const createThreadInteractions = useSidebarThreadInteractionsFactory();
    const interactions = useMemo(() => createThreadInteractions(getRecentThreadInteractionTarget(item, item.uuid)), [createThreadInteractions, item]);
    const onDelete = useCallback(() => onDeleteClick(item), [onDeleteClick, item]);
    const prefetchThreadEntries = usePrefetchThreadEntries({
        // Entry UUID, never the link segment: REST fallback links can carry a title slug.
        slugOrUUID: item.uuid,
        surface: isComputerTask ? 'computer' : 'search',
        enabled: isThread && !isStreaming,
    });
    const handleClick = interactions.onSelect;
    const handleHoverDetailsTitleClick = useCallback(() => {
        handleClick();
        track(sidebarSubmenuLinkClickedEvent({
            name: 'sidebar submenu link clicked',
            data: {
                href: link,
                text: 'thread-hover-details-title',
            },
        }));
    }, [handleClick, link, track]);
    const handleMarkThreadRead = interactions.markRead;
    const handleMarkThreadUnread = interactions.markUnread;
    const handleProjectAction = interactions.addOrMoveToProject;
    const deleteLabel = useMemo(() => isThread
        ? $t({
            defaultMessage: 'Delete',
            description: 'Context menu label to delete a thread from sidebar history',
            id: 'd69deb5b05',
        })
        : $t({
            defaultMessage: 'Delete page',
            description: 'Context menu label to delete a page from sidebar history',
            id: 'c3484d47a7',
        }), [$t, isThread]);
    const deleteThreadConfirmation = useDeleteThreadConfirmation(displayTitle, isComputerTask ? 'task' : 'thread');
    const deleteConfirmation = useMemo(() => isThread
        ? deleteThreadConfirmation
        : {
            title: $t({
                defaultMessage: 'Delete page',
                description: 'Title of the confirmation modal when deleting a page from sidebar history',
                id: 'db96bfb6a9',
            }),
            bodyText: $t({
                defaultMessage: 'Are you sure you want to delete this page?',
                description: 'Body text of the confirmation modal when deleting a page from sidebar history',
                id: 'cafd1de0f0',
            }),
            confirmButtonTitle: $t({
                defaultMessage: 'Delete',
                description: 'Confirm button label when deleting a page from sidebar history',
                id: '2e226062f1',
            }),
        }, [$t, deleteThreadConfirmation, isThread]);
    const { menuItems: baseMenuItems } = useSidebarThreadActions({
        title,
        contextUUID,
        onDelete,
        showRename: isThread,
        deleteLabel,
        deleteConfirmation,
        threadRenameAnalytics: {
            surface: 'sidebar',
            threadType: isComputerTask ? 'computer' : 'search',
        },
    });
    const readStateMenuItems = useMemo(() => {
        if (!isThread || !contextUUID)
            return [];
        return [
            unread
                ? {
                    text: $t({
                        defaultMessage: 'Mark as read',
                        description: 'Context menu label to mark a sidebar thread as read',
                        id: 'c4887d0204',
                    }),
                    icon: $icon('mail-opened'),
                    onClick: handleMarkThreadRead,
                }
                : {
                    text: $t({
                        defaultMessage: 'Mark as unread',
                        description: 'Context menu label to mark a sidebar thread as unread',
                        id: '45dbeece6f',
                    }),
                    icon: $icon('mail'),
                    onClick: handleMarkThreadUnread,
                },
        ];
    }, [$t, handleMarkThreadRead, handleMarkThreadUnread, isThread, contextUUID, unread]);
    const projectMenuItems = useMemo(() => {
        if (!isThread || !contextUUID)
            return [];
        return [
            {
                text: collectionUUID
                    ? $t({
                        defaultMessage: 'Move to project',
                        description: 'Context menu label to move a sidebar thread to a different project',
                        id: '82879e630e',
                    })
                    : $t({
                        defaultMessage: 'Add to project',
                        description: 'Context menu label to add a sidebar thread to a project',
                        id: '562340e3f0',
                    }),
                icon: collectionUUID ? $icon('arrows-shuffle') : $icon('plus'),
                onClick: handleProjectAction,
            },
        ];
    }, [$t, collectionUUID, handleProjectAction, isThread, contextUUID]);
    const menuItems = useMemo(() => [
        ...(additionalMenuItems ?? []),
        ...projectMenuItems,
        ...readStateMenuItems,
        ...baseMenuItems,
    ], [additionalMenuItems, baseMenuItems, projectMenuItems, readStateMenuItems]);
    return (_jsx(SessionItem, { kind: isComputerTask ? 'computer' : 'search', href: link, title: displayTitle, sidebarOpen: sidebarOpen, isInPopover: isInPopover, menuItems: menuItems, loading: isStreaming, onPrefetch: prefetchThreadEntries, onSelect: handleClick, accessory: getThreadItemAccessory({ unread: !!unread }), hideAccessoryWhenSelected: !groupedHistoryEnabled, collapsed: collapsed, foreground: foreground, hoverDetails: {
            updatedAt: item.updated_at,
            answerPreview: item.answer_preview,
            projectTitle: item.projectTitle,
            projectIcon: item.projectIcon,
            detailsLookupEntryUuid: hoverDetailsLookupEntryUuid,
            unread: item.unread,
            onTitleClick: handleHoverDetailsTitleClick,
        } }));
});
//# sourceMappingURL=ThreadItem.js.map